/**
 * 主軸カテゴリー (product_categories) の型定義。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md (v3.1.3 追加)
 *
 * 物理コレクション: product_categories/{categoryId}
 *
 * 役割:
 *   - Deal.primaryCategoryId / Form.primaryCategoryId が参照するマスタ
 *   - 旧来は Deal.primaryCategory に文字列リテラルを直接保存していたが、
 *     v3.1.3 で ID 参照 + 表示用スナップショットの2層構造に移行
 *   - カテゴリー名の変更が、関連する商談・フォームに動的反映される
 *
 * 並走運用:
 *   - Deal.primaryCategory (文字列) は表示用スナップショットとして残す
 *     (カテゴリーが削除された場合のフォールバック表示用)
 *   - Deal.primaryCategoryId が真のソース
 */
import type { BankAccount, CompanyInfo, IsoOrTimestamp } from './common';

/**
 * 書類（見積書・請求書）に表示するロゴの位置プリセット。
 * 印刷崩れを避けるため、上段3点に固定。
 */
export type CategoryLogoPosition = 'top-left' | 'top-center' | 'top-right';

/**
 * カテゴリー別の請求書振込先口座 (v3.3.x)。
 * `BankAccount` から `id` / `isDefault` を除いた形で、`InvoiceBankAccountSnapshot`
 * と構造が一致する（請求書発行時にそのままスナップショットへコピーされる）。
 */
export type CategoryBankAccount = Pick<
  BankAccount,
  | 'label'
  | 'bankName'
  | 'branchName'
  | 'accountType'
  | 'accountNumber'
  | 'accountHolder'
>;

export const CATEGORY_LOGO_POSITION_DEFAULT: CategoryLogoPosition = 'top-right';

/**
 * 顧客タイプ。カテゴリーがどの市場向けかを表す。
 * Phase I (product_lines 統合) で旧 ProductLine.kind から移植。
 */
export type ProductCategoryKind = 'btob' | 'btoc' | 'mixed';

export const PRODUCT_CATEGORY_KIND_DEFAULT: ProductCategoryKind = 'mixed';

export interface ProductCategory {
  /** 表示名 (例: "プロ機器", "コンシューマ機器") */
  name: string;
  /** 任意の説明 */
  description?: string | null;
  /** バッジ色などの表示ヒント (任意、未設定なら既定色) */
  color?: string | null;
  /** 表示順 (昇順、未設定は末尾) */
  displayOrder: number;
  /** false で論理停止。商談ボードのフィルタ等から除外。 */
  isActive: boolean;
  /**
   * 顧客タイプ (btob/btoc/mixed)。
   * Phase I で旧 product_lines.kind を移植。未設定は 'mixed' 扱い (後方互換)。
   */
  kind?: ProductCategoryKind | null;

  /**
   * 書類（見積書・請求書等）に表示するロゴ画像の URL。
   * Storage の `system/imgs/*` に配置されたダウンロード URL を保存。
   * null = 表示なし。
   */
  logoStoragePath?: string | null;
  /**
   * ロゴの表示位置プリセット。デフォルト 'top-right'。
   */
  logoPosition?: CategoryLogoPosition | null;

  /**
   * このカテゴリーに紐づく Deal から発信する自動メールの送信元 mail_account ID。
   * null/undefined = 営業設定の autoMailFromAccountId（既定）にフォールバック。
   * 仕様書: docs/01_spec/spec_user_management.md / U-9
   */
  mailAccountId?: string | null;

  /**
   * 同意ステップ (`agreed` ステージ) を経由させるか。
   * - true  : 成約 (won) → 同意 (agreed) → 入金 (paid) のフロー
   * - false : 成約 (won) → 入金 (paid) 直行（同意タブ・同意確認ラベル・同意ボードカラム全て非表示）
   * - undefined : 後方互換のため false 扱い
   *
   * v3.2.25 で導入。Phase 2+ で同意書管理画面・公開画面が UI 連動する。
   */
  requiresConsent?: boolean;

  /**
   * メーカー (発注先) のメールアドレス (v3.2.28+)。
   * TimeWaver などの仕入フローで「メーカー見積依頼」「メーカー正式発注」を自動送信する宛先。
   * 設定されていれば window.prompt() による都度入力を省略してこのアドレスに送信する。
   * null/undefined のときは「メーカーアドレス未設定」エラーで送信ボタンを抑止する。
   */
  manufacturerEmail?: string | null;

  /**
   * メーカー (発注先) メールアドレス（複数, v3.3.x+）。
   * 「メーカー見積依頼」「メーカー正式発注」メールの宛先 (To)。複数メーカー / 複数担当に対応。
   * 後方互換: 旧単一の `manufacturerEmail` は先頭要素として保持し、未設定環境の読み出しフォールバックに使う。
   */
  manufacturerEmails?: string[] | null;

  /**
   * メーカー宛メールの既定 CC（複数, v3.3.x+）。
   * 「メーカー見積依頼」「メーカー正式発注」送信時に自動で CC に挿入される。
   */
  manufacturerCcEmails?: string[] | null;

  /**
   * メーカー取引通貨（発注通貨）の ISO コード (例: 'EUR' / 'USD' / 'JPY', v3.3.x+)。
   * 単一の情報源として、発注カード・送金依頼メール・経理の送金依頼リスト・送金完了メールの
   * 金額表示に使う。null/undefined = 既定 EUR (従来挙動)。
   */
  manufacturerCurrency?: string | null;

  /**
   * このカテゴリーで発行する請求書の既定振込先口座 (v3.3.x)。
   * - 設定時: 請求書発行 (markQuoteWon) のスナップショットに **最優先** で採用される。
   * - null/undefined: オーナー (admins.isOwner) の既定口座にフォールバック。
   * 過去発行の請求書は発行時の Invoice.bankAccountSnapshot で固定されるため、
   * ここを後から変更しても既発行分の表示は変わらない。
   */
  bankAccount?: CategoryBankAccount | null;

  /**
   * このカテゴリーの請求書・見積書・関連書類で使う発行元会社情報の上書き (v3.3.x)。
   * - 設定時: settings/general.companyInfo (デフォルト) を丸ごと上書き。
   * - null/undefined: デフォルトを使用。
   * 解決は functions/src/common/companyInfo.ts の resolveCompanyInfo() に集約。
   */
  companyInfo?: CompanyInfo | null;

  /**
   * @deprecated v3.2.9 で廃止。`commission_sets.isActive=true` の単一セットに統合。
   * 旧データは無視される（後方互換のため型は残置）。
   */
  activeCommissionSetId?: string | null;

  /**
   * このカテゴリーに紐づくブランド名（メーカー）の配列 (v3.2.21+)。
   * 商品カタログの category フィールドと一致するブランド名を持つ。
   *
   * 各ブランドの「適用中 (isActive=true)」のカタログから商品を自動取得する。
   * これにより、商品カタログのバージョン切替時にカテゴリ側の手動更新が不要になる
   * （ヒューマンエラー防止）。
   */
  linkedBrandNames?: string[];

  /**
   * @deprecated v3.2.21 以降は linkedBrandNames を使う。
   * 後方互換のため残置 — 読み込み時は linkedBrandNames が優先、未設定なら catalogIds を使う。
   */
  catalogIds?: string[];

  /** soft delete (cascade purge は将来実装) */
  _deleted: boolean;
  _deletedAt?: IsoOrTimestamp | null;

  createdAt: IsoOrTimestamp;
  createdBy: string;
  updatedAt: IsoOrTimestamp;
}
